import { Room, version } from 'twilio-video';
import { version as processorVersion } from '@twilio/video-processors';
import app from '../../../package.json';
type Event = { at: string; event: string; code?: number };
let report = {
	appVersion: app.version,
	sdkVersion: version,
	processorVersion,
	browser: navigator.userAgent,
	platform: navigator.platform,
	roomSid: '',
	participantSid: '',
	events: [] as Event[],
	samples: [] as object[],
};
export function recordCallEvent(event: string, code?: number) {
	report.events.push({
		at: new Date().toISOString(),
		event,
		...(typeof code === 'number' ? { code } : {}),
	});
	report.events = report.events.slice(-200);
}
export function diagnosticReport() {
	return JSON.stringify(report, null, 2);
}
export function observeCall(room: Room) {
	report = {
		...report,
		roomSid: room.sid,
		participantSid: room.localParticipant.sid,
		events: [],
		samples: [],
	};
	recordCallEvent('connected');
	let active = true;
	let sampling = false;
	const sample = async () => {
		if (!active || sampling) return;
		sampling = true;
		try {
			const stats = await room.getStats();
			if (!active) return;
			const tracks = stats
				.flatMap((stat) => [
					...stat.localVideoTrackStats,
					...stat.remoteVideoTrackStats,
				])
				.map((track) => ({
					frameRate: track.frameRate,
					packetsLost: track.packetsLost,
					roundTripTime:
						'roundTripTime' in track
							? track.roundTripTime
							: undefined,
				}));
			report.samples.push({
				at: new Date().toISOString(),
				networkQuality: room.localParticipant.networkQualityLevel,
				audioTracks: room.localParticipant.audioTracks.size,
				videoTracks: room.localParticipant.videoTracks.size,
				tracks,
			});
			report.samples = report.samples.slice(-60);
		} catch {
			recordCallEvent('statistics-unavailable');
		} finally {
			sampling = false;
		}
	};
	const reconnecting = (error: { code?: number }) =>
		recordCallEvent('reconnecting', error?.code);
	const reconnected = () => recordCallEvent('reconnected');
	const disconnected = (_: Room, error?: { code?: number }) => {
		recordCallEvent('disconnected', error?.code);
		cleanup();
	};
	const timer = setInterval(() => {
		void sample();
	}, 5000);
	const cleanup = () => {
		active = false;
		clearInterval(timer);
		room.off('reconnecting', reconnecting);
		room.off('reconnected', reconnected);
		room.off('disconnected', disconnected);
	};
	room.on('reconnecting', reconnecting);
	room.on('reconnected', reconnected);
	room.on('disconnected', disconnected);
	return cleanup;
}
export async function copyDiagnostics() {
	const text = diagnosticReport();
	try {
		await navigator.clipboard.writeText(text);
		return 'Call diagnostics copied.';
	} catch {
		const url = URL.createObjectURL(
			new Blob([text], { type: 'application/json' }),
		);
		const link = document.createElement('a');
		link.href = url;
		link.download = 'call-diagnostics.json';
		document.body.appendChild(link);
		link.click();
		link.remove();
		setTimeout(() => URL.revokeObjectURL(url), 1000);
		return 'Call diagnostics downloaded.';
	}
}
